import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Constants } from "../../../Constants/ApiConstants";
import { EndPoints } from "../../../Constants/endpoints";
import { useToast } from "../../../Services/ToastContext";
import { useGetData, usePostData } from "../../../hooks";
import { GetAllCertificates } from "../../../interface/GeneralResponse";
import { EditCertificateRequest } from "../../../interface/ProfileInterfaces";
import {
  DateInput,
  FileUpload,
  GeneralButton,
  GeneralInput,
} from "../../../components";
import { AppConfig } from "../../../api/config/config";
import { FILETYPES, FILETYPESWithoutHeifHeic } from "../../../Constants/fileConstants";

function EditDoctorCertificate({
  id,
  onSuccess,
}: {
  id: number | string;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const editCertificateSchema = z.object({
    Name: z
      .string()
      .nonempty({ message: t("required_field") })
      .optional(),
    Description: z.string().optional(),
    Date: z
      .string()
      .nonempty({ message: t("required_field") })
      .refine((val) => val !== "", {
        message: t("required_field"),
      }),
    Authority: z.string().optional(),
    File: z.instanceof(File).optional(),
  });
  const { showToast } = useToast();

  const { data } = useGetData<GetAllCertificates>(
    EndPoints.GetCertificates,
    {},
    Number(id),
  );

  const [file, setFile] = useState<File | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [showDefaultImage, setShowDefaultImage] = useState<boolean>(false);

  const { mutate: editCertificate, isPending } = usePostData(
    `${EndPoints.EditCertificates}/${id}`,
  );

  type EditCertificateFormData = z.infer<typeof editCertificateSchema>;

  const methods = useForm<EditCertificateFormData>({
    resolver: zodResolver(editCertificateSchema),
    defaultValues: {
      Name: "",
      Description: "",
      Date: "",
      Authority: "",
      File: undefined,
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    reset,
  } = methods;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (data && data.Data) {
      reset({
        Name: data.Data.Name || "",
        Description: data.Data.Description || "",
        Date: data.Data.Date,
        Authority: data.Data.Authority || "",
      });
      setShowDefaultImage(!!data.Data.Image);
    }
  }, [data, reset]);

  const onSubmit = (data: EditCertificateFormData) => {
    if (!file && !showDefaultImage) {
      setFileError("required");
      return;
    }

    const requestData: EditCertificateRequest = {
      Name: data.Name,
      Description: data.Description,
      Date: data.Date,
      Authority: data.Authority,
      ...(file ? { File: file } : { FileNotChanged: true }),
    };

    const formData = new FormData();

    Object.entries(requestData).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      if (key === "File" && value instanceof File) {
        formData.append(key, value);
        return;
      }

      if (key === "FileNotChanged") {
        formData.append("FileNotChanged", "true");
        return;
      }

      formData.append(key, value as any);
    });

    editCertificate(formData, {
      onSuccess: () => {
        showToast(t("edited_successfully"), "success");
        reset();
        queryClient.refetchQueries({
          queryKey: [EndPoints.GetCertificates],
          exact: false,
        });
        setFile(null);
        setUploadSuccess(false);
        setFileError(null);
        onSuccess();
      },
      onError: (error: any) => {
        showToast(error.message || t("failed_to_add_Certificate"), "error");
      },
    });
  };

  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex max-h-[73dvh] flex-col gap-5 overflow-y-auto py-3">
            <GeneralInput
              name="Name"
              label={t("title")}
              placeholder={t("title")}
            />
            <DateInput
              name="Date"
              label={t("certificate_date")}
              required
              dateConstraint="past"
              aria-describedby="Date-description"
              hideErrorMessage
            />
            {errors.Date && (
              <p className="text-xs text-error">{t("required_field")}</p>
            )}

            <GeneralInput
              name="Authority"
              label={t("where_did_you_get_your_certificate")}
              placeholder={t("where_did_you_get_your_certificate")}
            />
            <GeneralInput
              type="textarea"
              name="Description"
              label={t("certificate_details")}
              placeholder={t("certificate_details")}
              maxLength={250}
            />
            <FileUpload
              accept={FILETYPESWithoutHeifHeic}
              onFileChange={(selectedFile) => {
                setFile(selectedFile);
                setFileError(null);
                setUploadSuccess(true);
              }}
              file={file}
              onCancel={() => {
                if (showDefaultImage) {
                  setShowDefaultImage(false);
                }
                setFile(null);
                setUploadSuccess(false);
                setFileError("required");
              }}
              uploadSuccess={uploadSuccess}
              errorMessageKey={fileError ? "required" : undefined}
              defaultImage={
                showDefaultImage && data?.Data?.Image
                  ? `${AppConfig.SERVER_URL}${data.Data.Image}`
                  : undefined
              }
              label={t("image_certificate")}
            />
          </div>
          <div className="mt-5 flex justify-end gap-4">
            <GeneralButton
              type="button"
              fontColor="body-text"
              title={t("cancel")}
              color="lite"
              onClick={onSuccess}
            />
            <GeneralButton
              type="submit"
              className="px-10"
              isLoading={isPending}
              title={t("edit")}
              color="blue"
            />
          </div>
        </form>
      </FormProvider>
    </div>
  );
}

export default EditDoctorCertificate;
